import React, { useState } from 'react';

import dynamic from 'next/dynamic';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import type { SxProps } from '@mui/material';

import { useWindowSize } from 'react-use';
import { useTranslation } from 'next-i18next';

import StoriesModal from 'src/components/news/StoriesModal';
import Avatar from 'src/components/ui/Avatar';
import AvatarLightBox from 'src/components/ui/AvatarLightBox';
import Icon from 'src/components/ui/Icon';
import Image from 'src/components/ui/Image';
import Lightbox from 'src/components/ui/Lightbox';
import type { LinkProps } from 'src/components/ui/Link';
import Link from 'src/components/ui/Link';
import Typography from 'src/components/ui/Typography';
import { breakpoints } from 'src/constants/breakpoints';
import { colours } from 'src/constants/colours';
import { paddingAroundCoverInPx, ratioCover } from 'src/constants/misc';

import theme from 'src/utils/theme';

import useActivityToDisplay from 'src/hooks/useActivityToDisplay';
import useAppRouter from 'src/hooks/useAppRouter';
import useModalOpenState from 'src/hooks/useModalOpenState';
import useUser from 'src/hooks/useUser';

import { routePathCompany, routePathUser } from 'src/constants/router';
import type { Medium, Owner, UserProfileQuery } from 'src/types/__generated__/graphql';
import { styledComponent, styledTag } from 'src/utils/styled';
import UserCTAs from 'src/components/profile/UserCTAs';
import Button from 'src/components/ui/Button';
import type { FollowTab } from 'src/components/profile/follow/FollowModal';
import FollowModal from 'src/components/profile/follow/FollowModal';
import { formatCityNameAndZipCode } from 'src/utils/addressUtils';
import useBreakpoints from 'src/hooks/useBreakpoints';

const LogoWoopenDefault = dynamic(() => import('src/assets/logos/logo-woopen-default.svg'), {
    ssr: false,
});

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0 0 0;
    &:last-child {
        padding-bottom: 0;
    }
`;

const StyledAvatar = styledComponent(Avatar)`
    font-size: 3rem;
    border: 0.1875rem solid ${colours.white.main};
`;

const AvatarCardWrapper = styledTag('div')<{
    $hasUnreadStory?: boolean;
}>`
    position: absolute;
    left: 2rem;
    top: -6rem;
    padding: 0.225rem;
    height: 7.7rem;
    border-radius: 5rem;
    background: ${({ $hasUnreadStory }) =>
        $hasUnreadStory ? 'linear-gradient(104.83deg, #132e48 -10.97%, #0072da 83.61%)' : String(colours.white.main)};
    cursor: pointer;

    @media screen and (max-width: ${breakpoints.tabletPortrait - 1}px) {
        left: 50%;
        top: -2rem;
        transform: translate(-50%, -50%);
    }
`;

const StyledLink = styledComponent((props: LinkProps) => <Link {...props} />)`
    color: ${theme.palette.text.primary};
    font-weight: bold;
    white-space: nowrap;
    margin-left: 0.2rem;
`;

const StyledBox = styledComponent(Box)`
    cursor: pointer;
`;

const AddressContainer = styledComponent(Box)`
    display: flex;
    align-items: center;
    @media screen and (max-width: ${breakpoints.tabletPortrait - 1}px) {
        justify-content: center;
    }
`;

const StyledLogoWoopenDefault = styledComponent(LogoWoopenDefault)`
    position: absolute;
    width: 10.375rem;
    height: 7.75rem;
`;

const StyledCompanyAvatar = styledComponent(Avatar)`
    margin-left: 0.25rem;
    margin-right: 0.25rem;
`;
const StyledRow = styledComponent(Box)`
    > :last-child {
        margin-left: 0
    }
`;

const linkButtonStyle: SxProps = {
    textDecoration: 'unset !important',
    '&:hover': {
        textDecoration: 'underline !important',
    },
};

export interface HeaderSectionProps {
    userProfile: NonNullable<UserProfileQuery['userProfile']>;
    bottomMargin?: boolean;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({ userProfile, bottomMargin }) => {
    const { t } = useTranslation();
    const { width } = useWindowSize();
    const currentUser = useUser();
    const router = useAppRouter();
    const [openedStoriesOwner, setOpenedStoriesOwner] = useState<Owner | undefined>(undefined);
    const { props: coverPictureLightboxProps, open: openCoverPictureLightBoxModal } = useModalOpenState();
    const { props: avatarLightboxProps, open: openAvatarLightBoxModal } = useModalOpenState();
    const { props: followModalProps, open: openFollowModal } = useModalOpenState();
    const [followModalSelectedTab, setFollowModalSelectedTab] = useState<FollowTab>('following');

    const fullName = userProfile.fullName;
    const mainImage = String(
        userProfile.coverImage?.url ?? `${process.env.NEXT_PUBLIC_SITE_URL}/assets/images/default_image.png`,
    );
    const avatarAlt = userProfile.initials;
    const ratingNumString = userProfile.numberOfRatings > 300 ? `300+` : userProfile.numberOfRatings;

    // Max 1 number after decimal
    const averageRating = Math.round(Number(userProfile.averageRating) * 10) / 10;

    const isSelf = userProfile.id === currentUser?.id;

    const groupLogoOfUser = userProfile.affiliatedCompany?.group?.image;

    const getActivityToDisplay = useActivityToDisplay();

    const { isMobile } = useBreakpoints();

    let companyLink;
    if (userProfile.companyToDisplay) {
        companyLink = !userProfile.companyToDisplay.capabilities?.hasPublicPage
            ? userProfile.companyToDisplay.website
            : `${routePathCompany}/${userProfile.companyToDisplay.id}`;
    }

    const handleAvatarClick = (open: () => void) => {
        if (userProfile.hasUnreadStory) setOpenedStoriesOwner({ user: { id: userProfile.id } } as Owner);
        else if (userProfile.image && !openedStoriesOwner) open();
    };

    const handleClickFollow = (tab: FollowTab) => {
        setFollowModalSelectedTab(tab);
        openFollowModal();
    };

    const displayLinkOfCompany = companyLink ? (
        <>
            {userProfile.companyToDisplay?.id && userProfile.companyToDisplay.capabilities?.hasPublicPage && (
                <StyledCompanyAvatar
                    isCompany={Boolean(userProfile.companyToDisplay)}
                    image={userProfile.companyToDisplay.image as Medium}
                />
            )}
            <StyledLink href={companyLink}>{userProfile.companyToDisplay?.name}</StyledLink>
        </>
    ) : (
        <Typography weight="bold" display="inline">
            {userProfile.companyToDisplay?.name}
        </Typography>
    );

    return (
        <Card elevation={0} sx={bottomMargin ? { mb: '1rem' } : {}}>
            {mainImage && (
                <>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        style={{ cursor: 'pointer' }}
                    >
                        <Image
                            src={mainImage}
                            alt={fullName ?? 'user-header'}
                            style={{
                                objectFit: 'cover',
                                aspectRatio: `${ratioCover}/1`,
                                width: '100%',
                                height: 'auto',
                            }}
                            width={width - paddingAroundCoverInPx}
                            height={(width - paddingAroundCoverInPx) / ratioCover}
                            onClick={() => userProfile.coverImage && openCoverPictureLightBoxModal()}
                        />
                        {!userProfile.coverImage?.url && <StyledLogoWoopenDefault />}
                    </Box>
                    {coverPictureLightboxProps.open && (
                        <Lightbox
                            {...coverPictureLightboxProps}
                            images={[userProfile.coverImage as Medium]}
                            mainImageUrl={userProfile.coverImage?.url}
                        />
                    )}
                </>
            )}
            <StyledCardContent>
                <AvatarCardWrapper
                    $hasUnreadStory={userProfile.hasUnreadStory}
                    onClick={() => handleAvatarClick(openAvatarLightBoxModal)}
                >
                    <StyledAvatar
                        data-target="user-avatar"
                        data-target-id="user-avatar"
                        alt={fullName}
                        image={userProfile.image as Medium}
                        fallbackText={avatarAlt}
                        size={116}
                    />
                    {openedStoriesOwner?.user?.id && (
                        <StoriesModal
                            openedOwner={openedStoriesOwner}
                            owners={[{ user: { id: userProfile.id } } as Owner]}
                            onClose={() => {
                                setOpenedStoriesOwner(undefined);
                            }}
                        />
                    )}
                </AvatarCardWrapper>
                {avatarLightboxProps.open && (
                    <AvatarLightBox {...avatarLightboxProps} image={userProfile.image as Medium} alt={fullName} />
                )}
                {groupLogoOfUser && (
                    <Box mt="-0.5rem" mr="1rem" display="flex" justifyContent="flex-end">
                        <StyledBox
                            onClick={() =>
                                router.push(`${routePathCompany}/${userProfile.affiliatedCompany?.group?.id}`)
                            }
                        >
                            <Avatar isCompany={Boolean(groupLogoOfUser)} image={groupLogoOfUser as Medium} />
                        </StyledBox>
                    </Box>
                )}
                <Box
                    display="flex"
                    mt={groupLogoOfUser ? '-0.90rem' : '1rem'}
                    flexDirection={isMobile ? 'column' : 'row'}
                    alignItems={isMobile ? 'center' : 'flex-end'}
                    justifyContent="space-between"
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                        pl={isMobile ? '1rem' : '0rem'}
                        pr={isMobile ? '1rem' : '0rem'}
                    >
                        <Typography
                            data-target="user-full-name"
                            data-target-id="user-fill-name"
                            variant="h1"
                            align={isMobile ? 'center' : 'left'}
                            color={colours.black.main}
                            noWrap
                        >
                            {fullName}
                        </Typography>
                        <Box
                            display="flex"
                            flexDirection="row"
                            width="100%"
                            justifyContent={isMobile ? 'center' : 'left'}
                        >
                            <Typography
                                variant="h4"
                                weight="normal"
                                display={!userProfile.customActivity ? 'flex' : undefined}
                                alignItems={!userProfile.customActivity ? 'center' : undefined}
                                style={{ marginTop: '0.5rem' }}
                                align={isMobile ? 'center' : 'left'}
                                color={colours.black.O87}
                            >
                                {getActivityToDisplay(userProfile)}
                                {userProfile.companyToDisplay && (
                                    <>
                                        {', '}
                                        {/* it's for display the link of company on one row if we don't have customActivity or in 2 row if we have customActivity */}
                                        {!userProfile.customActivity ? (
                                            displayLinkOfCompany
                                        ) : (
                                            <div>{displayLinkOfCompany}</div>
                                        )}
                                    </>
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {userProfile.city && (
                    <AddressContainer>
                        <Box ml={-0.25} mr={0.5} mt={0.25}>
                            <Icon size="1.25rem" name="Map" />
                        </Box>
                        <Typography color={colours.black.O87}>{formatCityNameAndZipCode(userProfile.city)}</Typography>
                    </AddressContainer>
                )}

                <StyledRow
                    display="flex"
                    mt={isMobile ? '1rem' : '0.5rem'}
                    alignItems={isMobile ? 'center' : 'flex-end'}
                    flexDirection={isMobile ? 'column' : 'row'}
                >
                    <Box display="flex" flexDirection="row">
                        {userProfile.isProfessionnal && (
                            <StyledBox
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                mr={2}
                                onClick={() => router.replace(`${routePathUser}/${userProfile.id}#rating-section`)}
                            >
                                <Box mr={0.5}>
                                    <Icon size="1.25rem" name="Star" color={theme.palette.tertiary.main} />
                                </Box>
                                <Typography color="dark">
                                    {averageRating} ({ratingNumString})
                                </Typography>
                            </StyledBox>
                        )}

                        <Box display="flex" flexDirection="row" alignItems="center">
                            <Icon name="NetworkFilled" size="1.25rem" />
                            <Button
                                variant="link"
                                sx={{ ...linkButtonStyle, ml: '0.3rem' }}
                                onClick={() => handleClickFollow('following')}
                            >
                                <Typography
                                    variant="body1"
                                    color={colours.black.O87}
                                    dangerouslySetInnerHTML={{
                                        __html: t('profile:followingHighlight', {
                                            highlight: userProfile.numberOfFollows,
                                            plurial: userProfile.numberOfFollows > 1 ? 's' : '',
                                        }),
                                    }}
                                />
                            </Button>
                            <Box
                                height="1rem"
                                mx={1.2}
                                style={{ borderRight: `0.0625rem solid ${colours.black.O16}` }}
                            />
                            <Button variant="link" sx={linkButtonStyle} onClick={() => handleClickFollow('followers')}>
                                <Typography
                                    variant="body1"
                                    color={colours.black.O87}
                                    dangerouslySetInnerHTML={{
                                        __html: t('profile:followersHighlight', {
                                            highlight: userProfile.numberOfFollowers,
                                            plurial: userProfile.numberOfFollowers > 1 ? 's' : '',
                                        }),
                                    }}
                                />
                            </Button>
                        </Box>
                    </Box>
                </StyledRow>
                <UserCTAs {...{ isSelf, isMobile, userProfile }} />
                {followModalProps.open && (
                    <FollowModal
                        {...followModalProps}
                        user={userProfile}
                        selectedTab={followModalSelectedTab}
                        setSelectedTab={setFollowModalSelectedTab}
                    />
                )}
            </StyledCardContent>
        </Card>
    );
};

export default HeaderSection;
